import React, { type ImgHTMLAttributes, useCallback, useState } from 'react';
import type { Interpolation } from 'styled-components';
import { clientAppLogImageError } from '@/app/common/utils/clientAppLogImageError';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useInitialParams } from './InitialParamsProvider';

export const DEFAULT_FALLBACK_IMAGE = 'room-no-images.png';

type PictureProps = {
  url: string;
  sources?: { srcSet: string; media?: string; sizes?: string }[];
  pictureCss?: Interpolation<object>;
  fallbackUrl?: string;
} & ImgHTMLAttributes<HTMLImageElement>;

export function getUrl(url: string) {
  if (url.includes('http:')) {
    return url.split('http:').join('https:');
  }
  return url;
}

export function Picture(props: PictureProps) {
  const { url, alt, sources, pictureCss, fallbackUrl, ...rest } = props;
  const { internalHotelId } = useInitialParams();
  const { localization } = usePageWrapperData();
  /**
   * The failure is remembered against the url that failed, not as a bare flag: the hero's slides are
   * keyed by position now, so this component is no longer remounted when the hotel's own photos
   * replace the ones borrowed from the results card. A bare flag would leave a slot whose borrowed
   * photo had 404'd showing the placeholder for good, even once it was handed a good url (MPH-1002).
   */
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const fallback = failedUrl === url;
  const { imageBasePath } = localization;
  const imgErrorLogsEnabled = localization?.configValuesApp?.imgErrorLogsEnabled;

  const src = fallback ? fallbackUrl || `${imageBasePath}${DEFAULT_FALLBACK_IMAGE}` : getUrl(url);

  const handleError = () => {
    setFailedUrl(url);
    if (imgErrorLogsEnabled) {
      clientAppLogImageError('Picture:handleError', src, internalHotelId);
    }
  };

  return (
    <picture css={pictureCss}>
      {!fallback &&
        sources?.map((source) => (
          <source key={source.srcSet} srcSet={getUrl(source.srcSet)} media={source.media} sizes={source.sizes} />
        ))}
      <img
        {...rest}
        alt={alt}
        src={src}
        onError={handleError}
        css={`
          ${fallback && 'object-fit:cover !important;'}
        `}
      />
    </picture>
  );
}

type ImageProps = {
  url: string | string[];
  fallbackUrl?: string;
} & ImgHTMLAttributes<HTMLImageElement>;

export default function ImageWithFallback(props: ImageProps) {
  const { url, fallbackUrl, alt, srcSet, sizes, ...rest } = props;
  const { internalHotelId } = useInitialParams();
  const { localization } = usePageWrapperData();
  const { imageBasePath } = localization;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFallback, setIsFallback] = useState(false);
  const imgErrorLogsEnabled = localization?.configValuesApp?.imgErrorLogsEnabled;

  // Convert url to array if it's a string
  const imageUrls = Array.isArray(url) ? url : [url];
  const currentUrl = isFallback
    ? fallbackUrl || `${imageBasePath}${DEFAULT_FALLBACK_IMAGE}`
    : imageUrls[currentImageIndex];
  const formattedUrl = currentUrl ? getUrl(currentUrl) : '';

  const handleImageError = useCallback(() => {
    // Try next image if available
    if (currentImageIndex < imageUrls.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    } else {
      // All images failed, use fallback
      setIsFallback(true);
    }
    if (imgErrorLogsEnabled) {
      clientAppLogImageError('ImageWithFallback:handleImageError', formattedUrl, internalHotelId);
    }
  }, [currentImageIndex, imageUrls.length]);

  return (
    <img
      {...rest}
      alt={alt}
      src={formattedUrl}
      srcSet={isFallback || !srcSet ? undefined : getUrl(srcSet)}
      sizes={sizes}
      onError={handleImageError}
    />
  );
}
